import { useEffect, useState } from 'react';

import { Box, ButtonBase, IconButton, Stack } from '@mui/material';

import { Icon } from '@/components/atoms/Icon/Icon';
import { radius } from '@/theme/radius';
import { PhotoViewer } from './PhotoViewer';

export interface ComposerPhotosProps {
  photos: File[];
  isDisabled?: boolean;
  onRemove: (photo: File) => void;
}

const THUMBNAIL_SIZE = 64;
/** How far each cross hangs past its thumbnail's corner, in px. */
const CROSS_OVERHANG = 10;
const ALT = 'Photo to send';

/** A local URL for the file, revoked once it is no longer shown. */
const useObjectUrl = (file: File) => {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  return url;
};

const Thumbnail = ({
  photo,
  isDisabled,
  onOpen,
  onRemove,
}: {
  photo: File;
  isDisabled?: boolean;
  onOpen: (url: string) => void;
  onRemove: () => void;
}) => {
  const url = useObjectUrl(photo);

  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <ButtonBase
        aria-label='View photo'
        onClick={() => url && onOpen(url)}
        sx={{
          width: THUMBNAIL_SIZE,
          height: THUMBNAIL_SIZE,
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
          bgcolor: 'custom.neutral.100',
        }}
      >
        {url && (
          <Box
            component='img'
            src={url}
            alt={ALT}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </ButtonBase>
      <IconButton
        aria-label='Remove photo'
        size='small'
        disabled={isDisabled}
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: -CROSS_OVERHANG,
          right: -CROSS_OVERHANG,
          bgcolor: 'background.paper',
          boxShadow: 1,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <Icon icon='xmark' fontSize={12} />
      </IconButton>
    </Box>
  );
};

/** ComposerPhotos shows the photos waiting to be sent, each one removable. */
export const ComposerPhotos = ({
  photos,
  isDisabled,
  onRemove,
}: ComposerPhotosProps) => {
  const [viewing, setViewing] = useState<string>();

  return (
    <>
      {/* Padded so the crosses, which overhang, are not clipped. */}
      <Stack
        direction='row'
        spacing={1.5}
        sx={{
          overflowX: 'auto',
          pt: `${CROSS_OVERHANG}px`,
          pr: `${CROSS_OVERHANG}px`,
        }}
      >
        {photos.map((photo, i) => (
          <Thumbnail
            key={i}
            photo={photo}
            isDisabled={isDisabled}
            onOpen={setViewing}
            onRemove={() => onRemove(photo)}
          />
        ))}
      </Stack>
      <PhotoViewer
        url={viewing}
        alt={ALT}
        onClose={() => setViewing(undefined)}
      />
    </>
  );
};

ComposerPhotos.displayName = 'ComposerPhotos';
export default ComposerPhotos;
